import { errorMessage } from "@atlas/shared";
import type { SocialSignInInput } from "@atlas/shared";
import { useState } from "react";
import type { CSSProperties } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Compass, UserPlus, X, Zap } from "lucide-react";
import { signup, socialSignIn } from "../../api/auth";
import { startCheckout } from "../../api/billing";
import { fetchPartnerCode, readStoredPartnerCode, clearStoredPartnerCode, storedPartnerCodeIsSilent } from "../../api/partners";
import { clearStoredReferral, readStoredReferral, storeReferral } from "../../api/referrals";
import { Badge } from "../../components/Badge";
import { MonthlyHistoryNotice, confirmMonthlyWithoutHistory } from "../../components/MonthlyHistoryNotice";
import { BetaGateModal } from "../../components/BetaGateModal";
import { useBetaGate } from "../../hooks/useFeatureFlags";
import { TurnstileWidget, TURNSTILE_SITE_KEY } from "../../components/TurnstileWidget";
import {
  FULL_HISTORY_GBP,
  PRO_ANNUAL_NOTE,
  PRO_ANNUAL_PER_MONTH_GBP,
  PRO_ANNUAL_TOTAL_GBP,
  PRO_FEATURES,
  PRO_MONTHLY_GBP,
  PRO_MONTHLY_NOTE,
} from "../../lib/pricing";
import { ConnectWithStravaButton, StravaConsentNote } from "../../components/StravaBrand";
import { SOCIAL_SIGN_IN_AVAILABLE, SocialSignInButtons } from "../../components/SocialSignInButtons";
import { useStravaConnectionsFull } from "../../hooks/useFeatureFlags";

function FieldGroup({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{children}</div>;
}

function Label({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} style={{
      fontSize: "var(--text-sm)",
      fontWeight: 600,
      color: "var(--text-secondary)",
      fontFamily: "var(--font-sans)",
    }}>
      {children}
    </label>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      style={{
        width: "100%",
        height: "var(--control-md)",
        padding: "0 12px",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        color: "var(--text-primary)",
        fontSize: "var(--text-base)",
        fontFamily: "var(--font-sans)",
        outline: "none",
        boxSizing: "border-box",
        ...props.style,
      }}
    />
  );
}

// ─── Plan selection step ──────────────────────────────────────────────────────

function BillingIntervalToggle({
  value,
  onChange,
}: {
  value: "month" | "year";
  onChange: (interval: "month" | "year") => void;
}) {
  const optionStyle = (active: boolean): CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    borderRadius: "var(--radius-pill)",
    border: "none",
    background: active ? "var(--surface-card)" : "transparent",
    color: active ? "var(--text-primary)" : "var(--text-muted)",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "var(--font-sans)",
    cursor: "pointer",
    boxShadow: active ? "var(--shadow-sm)" : "none",
  });

  return (
    <div
      role="group"
      aria-label="Billing period"
      style={{
        display: "inline-flex",
        padding: 4,
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-pill)",
        gap: 4,
      }}
    >
      <button type="button" style={optionStyle(value === "month")} onClick={() => onChange("month")}>
        Monthly
      </button>
      <button type="button" style={optionStyle(value === "year")} onClick={() => onChange("year")}>
        Annual <Badge variant="success">Save 25%</Badge>
      </button>
    </div>
  );
}

function PlanStep({ onContinueFree, onUpgradePro, upgrading }: {
  onContinueFree: () => void;
  onUpgradePro: (interval: "month" | "year") => void;
  upgrading: boolean;
}) {
  const [billingInterval, setBillingInterval] = useState<"month" | "year">("year");

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: "var(--space-2)" }}>
          One last thing
        </div>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)" }}>
          Choose your plan
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 8 }}>
          Start free and upgrade anytime.
        </p>
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
        <BillingIntervalToggle value={billingInterval} onChange={setBillingInterval} />
      </div>

      {billingInterval === "month" && (
        <div style={{ marginBottom: 12 }}>
          <MonthlyHistoryNotice
            compact
            onChooseYearly={() => setBillingInterval("year")}
            unlockText="You can unlock Full history any time from Plan and billing."
          />
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {/* Pro card */}
        <div style={{
          padding: "18px 20px",
          background: "var(--accent-soft)",
          border: "2px solid var(--border-gold)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--glow-gold-sm)",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: "var(--text-accent)" }}>Atlas Pro</span>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
                  background: "var(--accent)", color: "var(--text-on-gold)",
                  padding: "2px 6px", borderRadius: "var(--radius-pill)",
                }}>RECOMMENDED</span>
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, color: "var(--text-accent)", marginTop: 2 }}>
                £{billingInterval === "year" ? PRO_ANNUAL_PER_MONTH_GBP : PRO_MONTHLY_GBP}
                <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)" }}>/mo</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                {billingInterval === "year"
                  ? `Billed annually at £${PRO_ANNUAL_TOTAL_GBP}. ${PRO_ANNUAL_NOTE}`
                  : `Billed monthly. ${PRO_MONTHLY_NOTE}`}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
            {PRO_FEATURES.map((f) => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: "var(--text-secondary)" }}>
                <Check size={13} style={{ color: "var(--text-accent)", flexShrink: 0 }} />
                {f}
              </div>
            ))}
          </div>
          <button
            onClick={() => onUpgradePro(billingInterval)}
            disabled={upgrading}
            style={{
              width: "100%",
              height: 38,
              background: upgrading ? "var(--accent-press)" : "var(--accent)",
              color: "var(--text-on-gold)",
              border: "none",
              borderRadius: "var(--radius-md)",
              fontSize: 14,
              fontWeight: 600,
              cursor: upgrading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <Zap size={14} />
            {upgrading ? "Loading checkout…" : "Upgrade to Pro"}
          </button>
        </div>

        {/* Free card */}
        <button
          onClick={onContinueFree}
          style={{
            padding: "14px 20px",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-lg)",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>Continue with Free</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 3 }}>
            Peak and trail tracking, the last 12 months matched, 3 GPX uploads a month
          </div>
        </button>
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 2px 0", lineHeight: 1.5 }}>
          Want older walks matched without a subscription? Full history is £{FULL_HISTORY_GBP} once, from
          Plan and billing.
        </p>
      </div>
    </div>
  );
}

function partnerTrialLabel(days: number): string {
  if (days === 365) return "a year";
  if (days === 90) return "3 months";
  if (days === 30) return "a month";
  return `${days} days`;
}

// ─── SignupPage ───────────────────────────────────────────────────────────────

export default function SignupPage() {
  const betaGate = useBetaGate();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = useSearch({ strict: false }) as { invite?: string; ref?: string };
  // Partner code (ATL-134): ?invite= from the /with/{code} landing CTA wins,
  // else the code that page stashed in localStorage on an earlier visit.
  const [inviteCode] = useState<string | undefined>(
    () => search.invite?.trim().toLowerCase() || readStoredPartnerCode() || undefined,
  );
  // Invite a friend (spec 21): ?ref= from the join page CTA wins, else the
  // username the join page stashed in localStorage on an earlier visit.
  const [referralCode, setReferralCode] = useState<string | null>(() => {
    const fromSearch = search.ref?.trim().toLowerCase() || null;
    if (fromSearch) {
      storeReferral(fromSearch);
      return fromSearch;
    }
    return readStoredReferral();
  });
  const removeReferral = () => {
    clearStoredReferral();
    setReferralCode(null);
  };
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<"account" | "plan">("account");
  const [betaGateOpen, setBetaGateOpen] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileNonce, setTurnstileNonce] = useState(0);

  const inviteQuery = useQuery({
    queryKey: ["partner", inviteCode],
    queryFn: () => fetchPartnerCode(inviteCode!),
    enabled: !!inviteCode,
    retry: false,
  });
  // A resolved code is always sent (attribution); the trial is a separate
  // question the banner answers.
  const partner = inviteQuery.data?.partner;
  const trialOpen = !!partner && partner.proDays != null && !partner.full;
  // An ad's ?with= code attributes quietly: no "Google Ads sent you" banner
  // unless it carries a trial worth mentioning. A ?invite= from the landing
  // CTA is never silent.
  const [silentPartner] = useState(() => !search.invite && storedPartnerCodeIsSilent());
  const showPartnerBanner = !!partner && (trialOpen || !silentPartner);

  const signupMutation = useMutation({
    mutationFn: signup,
    onSuccess: (data) => {
      qc.setQueryData(["me"], { user: data.user });
      clearStoredReferral();
      clearStoredPartnerCode();
      // A successful trial claim already grants Pro — skip the plan step.
      if (data.partner?.trial.ok) {
        void navigate({ to: "/dashboard" });
      } else {
        setStep("plan");
      }
    },
    onError: () => {
      // Turnstile tokens are single-use — issue a fresh challenge for retry.
      setTurnstileToken(null);
      setTurnstileNonce((n) => n + 1);
    },
  });

  // Apple / Google (ATL-130): same post-signup plan step as email signups
  // when the account is new; an existing account just signs in.
  const [socialError, setSocialError] = useState<string | null>(null);
  const socialMutation = useMutation({
    mutationFn: (input: SocialSignInInput) => socialSignIn(input),
    onSuccess: (data) => {
      qc.setQueryData(["me"], { user: data.user });
      clearStoredReferral();
      clearStoredPartnerCode();
      if (!data.created || data.partner?.trial.ok) {
        void navigate({ to: "/dashboard" });
      } else {
        setStep("plan");
      }
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: (interval: "month" | "year") => startCheckout(interval),
    onSuccess: ({ url }) => {
      if (url) window.location.href = url;
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    signupMutation.mutate({
      name,
      email,
      password,
      ...(partner ? { inviteCode: partner.code } : {}),
      ...(referralCode ? { referralCode } : {}),
      ...(turnstileToken ? { turnstileToken } : {}),
    });
  };

  // Strava-first sign-ups are attributed server-side: GET /auth/strava?ref=
  // and ?with= set the cookies the callback reads when it creates a new user.
  const stravaParams = new URLSearchParams();
  if (referralCode) stravaParams.set("ref", referralCode);
  if (partner) stravaParams.set("with", partner.code);
  const stravaOAuthUrl = `${import.meta.env["VITE_API_URL"] ?? "/api"}/auth/strava${
    stravaParams.size ? `?${stravaParams.toString()}` : ""
  }`;
  const stravaFull = useStravaConnectionsFull();

  if (step === "plan") {
    return (
      <div style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-xl)",
        padding: "var(--space-8)",
        boxShadow: "var(--shadow-md), var(--ring-top)",
      }}>
        <PlanStep
          upgrading={checkoutMutation.isPending}
          onContinueFree={() => { void navigate({ to: "/dashboard" }); }}
          onUpgradePro={(interval) => {
            if (betaGate) {
              setBetaGateOpen(true);
              return;
            }
            if (interval === "month" && !confirmMonthlyWithoutHistory()) return;
            checkoutMutation.mutate(interval);
          }}
        />
        {checkoutMutation.isError && (
          <p style={{ fontSize: 13, color: "var(--status-danger)", marginTop: 12 }}>
            {errorMessage(checkoutMutation.error, "Failed to start checkout")}
          </p>
        )}
        {betaGateOpen && (
          <BetaGateModal
            onClose={() => setBetaGateOpen(false)}
            onRedeemedDone={() => { void navigate({ to: "/dashboard" }); }}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{
      background: "var(--surface-card)",
      border: "1px solid var(--border-subtle)",
      borderRadius: "var(--radius-xl)",
      padding: "var(--space-8)",
      boxShadow: "var(--shadow-md), var(--ring-top)",
    }}>
      <div style={{ marginBottom: "var(--space-7)" }}>
        <div className="eyebrow" style={{ marginBottom: "var(--space-2)" }}>
          Start exploring
        </div>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)" }}>
          Create your account
        </h1>
      </div>

      {partner && showPartnerBanner && (
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          padding: "10px 12px",
          marginBottom: "var(--space-5)",
          background: trialOpen ? "var(--accent-soft)" : "var(--surface-sunken)",
          border: `1px solid ${trialOpen ? "var(--border-gold)" : "var(--border-subtle)"}`,
          borderRadius: "var(--radius-md)",
        }}>
          <Compass size={14} style={{ color: trialOpen ? "var(--text-accent)" : "var(--text-muted)", flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            <span style={{ fontWeight: 600, color: trialOpen ? "var(--text-accent)" : "var(--text-primary)" }}>
              {partner.label ? `${partner.label} sent you` : "You came in through a partner link"}
            </span>
            {trialOpen
              ? ` — your account includes ${partnerTrialLabel(partner.proDays!)} of Atlas Pro.`
              : partner.proDays != null
                ? " — the Pro trials on this link have all been taken, but a free account is yours."
                : partner.landingLine
                  ? ` — ${partner.landingLine}`
                  : "."}
          </div>
        </div>
      )}

      {referralCode && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: "var(--space-5)",
          fontSize: 13,
          color: "var(--text-muted)",
        }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <UserPlus size={13} style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              Invited by <strong style={{ color: "var(--text-secondary)", fontWeight: 600 }}>{referralCode}</strong>
            </span>
          </span>
          <button
            type="button"
            onClick={removeReferral}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "var(--text-muted)",
              fontSize: 12,
              fontFamily: "var(--font-sans)",
              flexShrink: 0,
            }}
          >
            <X size={12} /> Not you? Remove
          </button>
        </div>
      )}

      {(SOCIAL_SIGN_IN_AVAILABLE || !stravaFull) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
          <SocialSignInButtons
            intent="signup"
            pending={socialMutation.isPending}
            {...(partner ? { inviteCode: partner.code } : {})}
            {...(referralCode ? { referralCode } : {})}
            onCredential={(input) => {
              setSocialError(null);
              socialMutation.mutate(input);
            }}
            onError={setSocialError}
          />
          {!stravaFull && (
            <ConnectWithStravaButton href={stravaOAuthUrl} height={44} fullWidth />
          )}
          {(socialMutation.error || socialError) && (
            <div style={{
              padding: "10px 12px",
              background: "var(--danger-soft)",
              border: "1px solid rgba(224, 80, 63, 0.4)",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--text-sm)",
              color: "var(--coral-300)",
            }}>
              {socialMutation.error
                ? errorMessage(socialMutation.error, "Couldn't create your account. Try again.")
                : socialError}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
            <span className="eyebrow">or with email</span>
            <div style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <FieldGroup>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="At least 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </FieldGroup>

        <TurnstileWidget
          onToken={setTurnstileToken}
          resetNonce={turnstileNonce}
        />

        {signupMutation.error && (
          <div style={{
            padding: "10px 12px",
            background: "var(--danger-soft)",
            border: "1px solid rgba(224, 80, 63, 0.4)",
            borderRadius: "var(--radius-md)",
            fontSize: "var(--text-sm)",
            color: "var(--coral-300)",
          }}>
            {errorMessage(signupMutation.error, "Couldn't create your account. Try again.")}
          </div>
        )}

        <button
          type="submit"
          disabled={signupMutation.isPending || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
          style={{
            width: "100%",
            height: "var(--control-lg)",
            background: signupMutation.isPending || (!!TURNSTILE_SITE_KEY && !turnstileToken)
              ? "var(--accent-press)"
              : "var(--accent)",
            color: "var(--text-on-gold)",
            border: "none",
            borderRadius: "var(--radius-md)",
            fontFamily: "var(--font-sans)",
            fontSize: "var(--text-base)",
            fontWeight: 600,
            cursor: signupMutation.isPending ? "not-allowed" : "pointer",
            transition: "background 0.15s ease",
          }}
        >
          {signupMutation.isPending ? "Creating account…" : "Create account"}
        </button>

      </form>

      <p style={{
        marginTop: "var(--space-5)",
        fontSize: 12,
        lineHeight: 1.5,
        color: "var(--text-muted)",
        textAlign: "center",
      }}>
        You pick a username next. Invite and partner codes from the link you arrived on are
        applied automatically.
      </p>
      {!stravaFull && <StravaConsentNote compact style={{ marginTop: "var(--space-3)" }} />}

      <p style={{
        marginTop: "var(--space-6)",
        textAlign: "center",
        fontSize: "var(--text-sm)",
        color: "var(--text-muted)",
        fontFamily: "var(--font-sans)",
      }}>
        Already have an account?{" "}
        <Link
          to="/login"
          style={{ color: "var(--text-accent)", fontWeight: 600 }}
        >
          Sign in →
        </Link>
      </p>
    </div>
  );
}
